import {
    Body,
    Box,
    browserSizes,
    FilmListItem,
    Grid,
    Heading,
    HtmlContent,
    Meta,
    ProgrammePart,
    Stack,
    Tag,
    TextButton,
    TicketListItem,
    useBrowserSize,
    Wrapper,
} from 'designsystem';
import React, { ComponentPropsWithRef, FC, Ref } from 'react';
import { ContentBlockTickets, getMetaForFilmOrProjectHit, TicketDatePicker, useGetApiImageProps } from 'shared';
import { CompositionDetailQuery } from '../../gql/api';
import { FormattedMessage } from 'react-intl';
import Link from 'next/link';
import slugify from 'slugify';
import useToggleCalendar from '../../hooks/useToggleCalendar';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';

type Ticket = Omit<
    ComponentPropsWithRef<typeof TicketListItem>,
    'buyTicketLabel' | 'onClick' | 'isLoading' | 'soldOutLabel'
> & {
    id: string;
    ticketType: string;
};

type SectionItem = CompositionDetailQuery['currentFestivalComposition']['sections'][number]['items'][number];

interface Props {
    composition: CompositionDetailQuery['currentFestivalComposition'];
    tickets: Ticket[];
    ticketBlockRef: Ref<HTMLDivElement>;
    showDatePicker: boolean;
}

const getSliderProps = (item: SectionItem, getImgProps: ReturnType<typeof useGetApiImageProps>) => {
    if (item.__typename === 'Film') {
        const directors = item.credits.filter(credit => credit.role.key === 'director');
        return {
            id: item.id,
            image: getImgProps(item.publications.favoriteImage, item.fullPreferredTitle),
            title: item.fullPreferredTitle,
            href: `/film/${item.id}/${slugify(item.fullPreferredTitle)}`,
            meta: {
                tag: item.isDocLab && <Tag>DocLab</Tag>,
                items: [
                    directors?.length > 0 && {
                        key: 'director',
                        value: directors.map(director => director.fullName).join(', '),
                    },
                    item.countriesOfProduction && {
                        key: 'countriesOfProduction',
                        value: item.countriesOfProduction.map(country => country.translation).join(', '),
                    },
                    item.yearOfProduction && {
                        key: 'yearOfProduction',
                        value: item.yearOfProduction,
                    },
                    item.lengthInMinutes && {
                        key: 'lengthInMinutes',
                        value: `${item.lengthInMinutes} min`,
                    },
                ].filter(Boolean),
            },
        };
    }
    return {
        id: item.id,
        image: getImgProps(item.publications.favoriteImage, item.fullTitle),
        title: item.fullTitle,
        href: `/composition/${item.id}/${slugify(item.fullTitle)}`,
    };
};

const CompositionDetailPageContent: FC<Props> = ({ composition, tickets, ticketBlockRef, showDatePicker }) => {
    const getImgProps = useGetApiImageProps();
    const browserSize = useBrowserSize();
    const smallScreen = browserSizes.indexOf(browserSize) <= browserSizes.indexOf('s');
    const { isAddedToCalendar, toggleCalendar, isLoadingCalendar } = useToggleCalendar();
    const { locale } = useRouter();
    return (
        <>
            <NextSeo title={composition?.fullTitle} titleTemplate="%s | IDFA Festival" />
            <Wrapper py={[9, null, null, 11]} order={[0, null, 1]} alignItems="center">
                <Grid gridTemplateRows="min-content 1fr" alignItems="start">
                    <Stack gridColumn={['1 / -1', null, null, '1 / span 6', '1 / span 7']} spacing={8}>
                        {composition?.description && (
                            <Box maxW={composition.special ? ['100%', null, null, '50%'] : '100%'}>
                                <Heading variant={3} mb={6}>
                                    <FormattedMessage defaultMessage="Beschrijving" />
                                </Heading>
                                <Body>
                                    <HtmlContent html={composition.description?.translation} />
                                </Body>
                            </Box>
                        )}
                        {composition?.shows?.length > 0 && !composition.special && (
                            <Box
                                flex={1}
                                display={['block', null, null, 'none']}
                                ref={smallScreen ? ticketBlockRef : null}
                                ml="auto"
                            >
                                {showDatePicker ? (
                                    <TicketDatePicker
                                        tickets={tickets}
                                        isAddedToCalendar={isAddedToCalendar}
                                        toggleCalendar={toggleCalendar}
                                        isLoadingCalendar={isLoadingCalendar}
                                        tooltipContent={
                                            <Body>
                                                <FormattedMessage
                                                    defaultMessage="Read <a>here</a> about the various festival locations in Amsterdam."
                                                    id="accessibility.tooltip"
                                                    values={{
                                                        a: children => (
                                                            <Link
                                                                href={
                                                                    locale === 'nl'
                                                                        ? '/plan-je-bezoek/locaties-en-toegankelijkheid/'
                                                                        : '/en/plan-your-visit/locations-and-accessibility/'
                                                                }
                                                            >
                                                                {children}
                                                            </Link>
                                                        ),
                                                    }}
                                                />
                                            </Body>
                                        }
                                    />
                                ) : (
                                    <ContentBlockTickets
                                        tickets={tickets}
                                        isAddedToCalendar={isAddedToCalendar}
                                        toggleCalendar={toggleCalendar}
                                        isLoadingCalendar={isLoadingCalendar}
                                        tooltipContent={
                                            <Body>
                                                <FormattedMessage
                                                    defaultMessage="Read <a>here</a> about the various festival locations in Amsterdam."
                                                    id="accessibility.tooltip"
                                                    values={{
                                                        a: children => (
                                                            <Link
                                                                href={
                                                                    locale === 'nl'
                                                                        ? '/plan-je-bezoek/locaties-en-toegankelijkheid/'
                                                                        : '/en/plan-your-visit/locations-and-accessibility/'
                                                                }
                                                            >
                                                                {children}
                                                            </Link>
                                                        ),
                                                    }}
                                                />
                                            </Body>
                                        }
                                    />
                                )}
                            </Box>
                        )}
                        {composition?.films?.length > 0 && (
                            <Stack spacing={6}>
                                <Heading variant={3}>
                                    <FormattedMessage defaultMessage="Samenstelling" />
                                </Heading>
                                {composition?.films?.map(film => (
                                    <Link
                                        prefetch={false}
                                        key={film.id}
                                        href={`/film/${film.id}/${slugify(film.fullPreferredTitle)}`}
                                    >
                                        <FilmListItem
                                            key={film.id}
                                            title={film.fullPreferredTitle}
                                            meta={
                                                <Meta
                                                    size="s"
                                                    {...getMetaForFilmOrProjectHit({ filmOrProject: film })}
                                                />
                                            }
                                            image={getImgProps(
                                                film?.publications?.favoriteImage,
                                                film?.fullPreferredTitle
                                            )}
                                            description={
                                                (film.intro?.translation || film.description?.translation) && (
                                                    <HtmlContent
                                                        html={film.intro?.translation || film.description.translation}
                                                    />
                                                )
                                            }
                                            translatedEditionTitle={
                                                <FormattedMessage defaultMessage="Festival history" />
                                            }
                                        />
                                    </Link>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                    {composition.shows?.length > 0 && !composition.special && (
                        <Box
                            display={['none', null, null, 'block']}
                            gridColumn={['1 / -1', null, null, '7 / span 6', '8 / span 5']}
                            gridRow={[null, null, null, '1 / -1']}
                            id="tickets"
                            scrollMarginTop={15}
                            ref={smallScreen ? null : ticketBlockRef}
                        >
                            {showDatePicker ? (
                                <TicketDatePicker
                                    tickets={tickets}
                                    isAddedToCalendar={isAddedToCalendar}
                                    toggleCalendar={toggleCalendar}
                                    isLoadingCalendar={isLoadingCalendar}
                                    tooltipContent={
                                        <Body>
                                            <FormattedMessage
                                                defaultMessage="Read <a>here</a> about the various festival locations in Amsterdam."
                                                id="accessibility.tooltip"
                                                values={{
                                                    a: children => (
                                                        <Link
                                                            href={
                                                                locale === 'nl'
                                                                    ? '/plan-je-bezoek/locaties-en-toegankelijkheid/'
                                                                    : '/en/plan-your-visit/locations-and-accessibility/'
                                                            }
                                                        >
                                                            {children}
                                                        </Link>
                                                    ),
                                                }}
                                            />
                                        </Body>
                                    }
                                />
                            ) : (
                                <ContentBlockTickets
                                    tickets={tickets}
                                    isAddedToCalendar={isAddedToCalendar}
                                    toggleCalendar={toggleCalendar}
                                    isLoadingCalendar={isLoadingCalendar}
                                    tooltipContent={
                                        <Body>
                                            <FormattedMessage
                                                defaultMessage="Read <a>here</a> about the various festival locations in Amsterdam."
                                                id="accessibility.tooltip"
                                                values={{
                                                    a: children => (
                                                        <Link
                                                            href={
                                                                locale === 'nl'
                                                                    ? '/plan-je-bezoek/locaties-en-toegankelijkheid/'
                                                                    : '/en/plan-your-visit/locations-and-accessibility/'
                                                            }
                                                        >
                                                            {children}
                                                        </Link>
                                                    ),
                                                }}
                                            />
                                        </Body>
                                    }
                                />
                            )}
                        </Box>
                    )}
                </Grid>
            </Wrapper>

            {composition.sections?.length > 0 && (
                <ProgrammePart
                    mb={[-8, -10, null, -18]}
                    mt={18}
                    textButton={
                        <TextButton size="s">
                            <FormattedMessage defaultMessage="Bekijk Programma" />
                        </TextButton>
                    }
                    slides={composition.sections[0].items.map(item => getSliderProps(item, getImgProps))}
                >
                    <Heading variant={2}>{composition.sections[0].name}</Heading>
                    {composition?.sections[0].description && (
                        <Body>
                            <HtmlContent html={composition.sections[0].description?.translation} />
                        </Body>
                    )}
                </ProgrammePart>
            )}
        </>
    );
};

export default CompositionDetailPageContent;
